import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  MdAssignment,
  MdChevronRight,
  MdErrorOutline,
  MdHome,
  MdOutlineQuestionAnswer,
  MdPerson,
  MdRateReview,
  MdSchool,
} from 'react-icons/md';
import { usePreferences } from '../../contexts/PreferencesContext';

const portalCrumb = { labelKey: 'authorPortal', path: '/' };

const getPageMeta = (pathname) => {
  let match;

  if (pathname === '/') {
    return {
      titleKey: 'authorDemandsTitle',
      descriptionKey: 'authorDemandsDescription',
      icon: MdAssignment,
      crumbs: [],
    };
  }

  if ((match = pathname.match(/^\/demandas\/show\/([^/]+)$/))) {
    return {
      titleKey: 'authorDemandDetailsTitle',
      descriptionKey: 'authorDemandDetailsDescription',
      icon: MdAssignment,
      crumbs: [portalCrumb],
    };
  }

  if ((match = pathname.match(/^\/atribuicao\/questoes\/register\/([^/]+)$/))) {
    return {
      titleKey: 'title_create_question',
      descriptionKey: 'authorCreateQuestionDescription',
      icon: MdOutlineQuestionAnswer,
      crumbs: [
        portalCrumb,
        { labelKey: 'authorQuestionsTitle', path: `/atribuicao/questoes/${match[1]}` },
      ],
    };
  }

  if (pathname.match(/^\/atribuicao\/questoes\/edit\/([^/]+)$/)) {
    return {
      titleKey: 'title_edit_question',
      descriptionKey: 'authorEditQuestionDescription',
      icon: MdOutlineQuestionAnswer,
      crumbs: [portalCrumb, { labelKey: 'authorQuestionsTitle' }],
    };
  }

  if ((match = pathname.match(/^\/atribuicao\/questoes\/([^/]+)$/))) {
    return {
      titleKey: 'authorQuestionsTitle',
      descriptionKey: 'authorQuestionsDescription',
      icon: MdOutlineQuestionAnswer,
      crumbs: [
        portalCrumb,
        { labelKey: 'authorDemandDetailsTitle', path: `/demandas/show/${match[1]}` },
      ],
    };
  }

  if (pathname === '/disciplinas') {
    return {
      titleKey: 'authorSubjectsTitle',
      descriptionKey: 'authorSubjectsDescription',
      icon: MdSchool,
      crumbs: [portalCrumb],
    };
  }

  if (pathname.match(/^\/disciplinas\/show\/([^/]+)$/)) {
    return {
      titleKey: 'authorSubjectQuestionsTitle',
      descriptionKey: 'authorSubjectQuestionsDescription',
      icon: MdSchool,
      crumbs: [portalCrumb, { labelKey: 'authorSubjectsTitle', path: '/disciplinas' }],
    };
  }

  if (pathname === '/recursos') {
    return {
      titleKey: 'authorAppealsTitle',
      descriptionKey: 'authorAppealsDescription',
      icon: MdRateReview,
      crumbs: [portalCrumb],
    };
  }

  if (pathname.match(/^\/recursos\/show\/([^/]+)$/)) {
    return {
      titleKey: 'authorAppealDetailsTitle',
      descriptionKey: 'authorAppealDetailsDescription',
      icon: MdRateReview,
      crumbs: [portalCrumb, { labelKey: 'authorAppealsTitle', path: '/recursos' }],
    };
  }

  if (pathname.match(/^\/recursos\/([^/]+)$/)) {
    return {
      titleKey: 'authorQuestionAppealsTitle',
      descriptionKey: 'authorQuestionAppealsDescription',
      icon: MdRateReview,
      crumbs: [portalCrumb, { labelKey: 'authorAppealsTitle', path: '/recursos' }],
    };
  }

  if (pathname === '/avaliar-questoes') {
    return {
      titleKey: 'authorEvaluationsTitle',
      descriptionKey: 'authorEvaluationsDescription',
      icon: MdOutlineQuestionAnswer,
      crumbs: [portalCrumb],
    };
  }

  if (pathname.match(/^\/avaliar-questoes\/show\/([^/]+)$/)) {
    return {
      titleKey: 'authorEvaluationDetailsTitle',
      descriptionKey: 'authorEvaluationDetailsDescription',
      icon: MdOutlineQuestionAnswer,
      crumbs: [portalCrumb, { labelKey: 'authorEvaluationsTitle', path: '/avaliar-questoes' }],
    };
  }

  if (pathname === '/questoes-recusadas') {
    return {
      titleKey: 'title_rejected_questions',
      descriptionKey: 'authorRejectedQuestionsDescription',
      icon: MdErrorOutline,
      crumbs: [portalCrumb],
    };
  }

  if (pathname.match(/^\/accounts\/profile\/([^/]+)$/)) {
    return {
      titleKey: 'myProfile',
      descriptionKey: 'authorProfileDescription',
      icon: MdPerson,
      crumbs: [portalCrumb],
    };
  }

  return {
    titleKey: 'authorPortal',
    descriptionKey: 'authorPortalDescription',
    icon: MdHome,
    crumbs: [],
  };
};

const ElaboradorPageHeader = ({ part = 'all' }) => {
  const { pathname } = useLocation();
  const { t } = usePreferences();
  const meta = getPageMeta(pathname);
  const PageIcon = meta.icon;
  const breadcrumbs = [
    ...meta.crumbs.filter((item) => item.labelKey !== 'authorPortal'),
    { labelKey: meta.titleKey },
  ];

  const breadcrumb = (
    <nav className="author-breadcrumb" aria-label={t('breadcrumb')}>
        {breadcrumbs.map((item, index) => {
          const isCurrent = index === breadcrumbs.length - 1;

          return (
            <React.Fragment key={`${item.labelKey}-${index}`}>
              {index > 0 && <MdChevronRight className="author-breadcrumb-separator" />}
              {item.path && !isCurrent ? (
                <Link to={item.path}>{index === 0 && <MdHome />}<span>{t(item.labelKey)}</span></Link>
              ) : (
                <span className={isCurrent ? 'current' : ''}>{index === 0 && <MdHome />}<span>{t(item.labelKey)}</span></span>
              )}
            </React.Fragment>
          );
        })}
    </nav>
  );

  const heading = (
    <div className="author-page-heading">
        <div className="author-page-icon" aria-hidden="true">
          <PageIcon />
        </div>
        <div>
          <h1>{t(meta.titleKey)}</h1>
          <p>{t(meta.descriptionKey)}</p>
        </div>
    </div>
  );

  if (part === 'breadcrumb') {
    return breadcrumb;
  }

  if (part === 'heading') {
    return <header className="author-page-header">{heading}</header>;
  }

  return (
    <header className="author-page-header">
      {breadcrumb}
      {heading}
    </header>
  );
};

export default ElaboradorPageHeader;
